import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2, Plus, Check, Gamepad2, Radio, Camera, Compass, ArrowUp, ArrowDown, ArrowUpDown,
  Bookmark, BookmarkCheck, Download, Trash2, UserPlus, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { discoveryAPI } from '../api/discovery';
import { getCountryFlag } from '../utils/formatters';
import PlatformIcon from './PlatformIcon';

// Discover mode of the Creators page: search the pool of streamers and channels
// the platform has SEEN — on the roster or not — by game, genre, language,
// country, time window and size, on Twitch, YouTube or both, then shortlist or
// add anyone to the platform. Every search reads the database only; the
// scheduled sweeps and scans keep it fresh, so there is no API cost per search.
// The one paid step is shortlist export, which looks up contact details from
// each creator's own bio (official APIs, capped per day, stored once).

// Always the brand's own casing (never CSS capitalize → "Youtube").
const PLATFORM_LABEL = { twitch: 'Twitch', youtube: 'YouTube', kick: 'Kick' };
const PLATFORMS = ['twitch', 'youtube', 'kick'];
// Twitch and Kick share the stream-shaped columns/metrics; YouTube is video-shaped.
const isStreamPlatform = (p) => p === 'twitch' || p === 'kick';

const LANGUAGE_NAME = {
  en: 'English', de: 'German', fr: 'French', es: 'Spanish', pt: 'Portuguese', ru: 'Russian',
  ja: 'Japanese', ko: 'Korean', it: 'Italian', zh: 'Chinese', pl: 'Polish', ar: 'Arabic',
  th: 'Thai', tr: 'Turkish', nl: 'Dutch', sv: 'Swedish', cs: 'Czech', hu: 'Hungarian',
  fi: 'Finnish', da: 'Danish', no: 'Norwegian', uk: 'Ukrainian', el: 'Greek', vi: 'Vietnamese',
  id: 'Indonesian', ro: 'Romanian', sk: 'Slovak', bg: 'Bulgarian', hr: 'Croatian', he: 'Hebrew',
  tl: 'Filipino', ms: 'Malay', hi: 'Hindi', ca: 'Catalan', asl: 'ASL', other: 'Other',
};
const languageName = (code) => LANGUAGE_NAME[code] || (code || '').toUpperCase();

let regionNames = null;
try { regionNames = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { regionNames = null; }
const countryName = (code) => {
  if (!code) return '';
  try { return regionNames ? regionNames.of(code.toUpperCase()) : code; } catch { return code; }
};

// Full numbers with thousands separators — no K/M shorthand anywhere in the table.
const full = (n, decimals = 0) => (n === null || n === undefined || Number.isNaN(Number(n)))
  ? '—'
  : Number(n).toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—');
const fmtDay = (iso) => (iso ? new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const WINDOWS = [3, 7, 14, 30, 60, 90];
const SHOW = [
  { key: 'all', label: 'Everyone' },
  { key: 'off', label: 'Not on platform' },
  { key: 'on', label: 'On platform' },
];
const DEFAULT_SORT = { twitch: 'peak', youtube: 'views', kick: 'peak' };
const PAGE_SIZE = 50;

const segBtn = (active) =>
  `px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
    active ? 'bg-sc-pink/20 border border-sc-pink/30 text-sc-pink' : 'bg-white/5 border border-white text-white hover:bg-white/10'
  }`;
const selectCls = 'bg-white/5 border border-white text-white text-xs rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-sc-pink/50 [&>option]:bg-gray-900';
const inputCls = 'bg-white/5 border border-white text-white text-xs rounded-lg px-2.5 py-1.5 placeholder-white/50 focus:outline-none focus:border-sc-pink/50';

// One platform's search: its own sort (the columns differ) and page.
// `enabled` false = platform not selected, nothing fetched.
function usePlatformSearch(platform, baseParams, enabled, canSearch) {
  const [sort, setSort] = useState(DEFAULT_SORT[platform]);
  const [dir, setDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Any change to filters or sort starts again from page 1.
  useEffect(() => { setPage(1); }, [baseParams, sort, dir]);

  const params = useMemo(() => ({ ...baseParams, platform, sort, dir, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }),
    [baseParams, platform, sort, dir, page]);

  useEffect(() => {
    if (!enabled || !canSearch) { setResults([]); setTotal(0); setLoading(false); setError(''); return undefined; }
    let cancelled = false;
    setLoading(true); setError('');
    const t = setTimeout(async () => {
      try {
        const r = await discoveryAPI.search(params);
        if (cancelled) return;
        setResults(r.data.results || []); setTotal(r.data.total || 0);
      } catch (e) {
        if (!cancelled) { setError(e.response?.data?.error || 'Search failed'); setResults([]); setTotal(0); }
      } finally { if (!cancelled) setLoading(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [params, enabled, canSearch]);

  // Column-header click: new column → descending; same column → flip.
  const sortBy = useCallback((k) => {
    if (k === sort) setDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSort(k); setDir('desc'); }
  }, [sort]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return { results, total, loading, error, sort, dir, sortBy, page, pages, setPage };
}

const CreatorDiscovery = ({ onAdded }) => {
  const navigate = useNavigate();
  const [platforms, setPlatforms] = useState(['twitch']);    // one or both
  const [activeTab, setActiveTab] = useState('twitch');       // which table shows when both are selected
  const [game, setGame] = useState(null);                     // { id, title, twitchGameId }
  const [gameQuery, setGameQuery] = useState('');
  const [gameResults, setGameResults] = useState([]);
  const [gameSearching, setGameSearching] = useState(false);
  const [gameAdding, setGameAdding] = useState(false);
  const [days, setDays] = useState(60);
  const [customRange, setCustomRange] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [language, setLanguage] = useState('');
  const [country, setCountry] = useState('');
  const [genre, setGenre] = useState('');     // IGDB game genre, e.g. "Role-playing (RPG)"
  const [onPlatform, setOnPlatform] = useState('off');
  const [minPeak, setMinPeak] = useState('');   // Twitch
  const [minViews, setMinViews] = useState(''); // YouTube
  const [meta, setMeta] = useState({ countries: [], languages: [], genres: [] });
  const [error, setError] = useState('');
  const [adding, setAdding] = useState({});      // `${platform}:${externalId}` -> true
  const [added, setAdded] = useState({});        // `${platform}:${externalId}` -> creatorId
  const gameBoxRef = useRef(null);

  // ---- shortlists -------------------------------------------------------------
  // Saving to a shortlist never touches the roster. Contact enrichment happens
  // on export (official APIs only, capped per day) and is stored on the pool
  // record, so a creator on several lists is looked up once.
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const [listItems, setListItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listBusy, setListBusy] = useState('');          // 'export' | 'addAll' | ''
  const [listNotice, setListNotice] = useState('');
  const [newListName, setNewListName] = useState('');
  const [shortlisted, setShortlisted] = useState({});    // `${platform}:${externalId}` -> true (for the active list)

  const loadLists = useCallback(async () => {
    try {
      const r = await discoveryAPI.shortlists();
      const ls = r.data.shortlists || [];
      setLists(ls);
      setActiveListId((cur) => (cur && ls.some((l) => l.id === cur) ? cur : (ls[0]?.id || '')));
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadLists(); }, [loadLists]);

  const loadListItems = useCallback(async (id) => {
    if (!id) { setListItems([]); setShortlisted({}); return; }
    try {
      setListLoading(true);
      const r = await discoveryAPI.shortlist(id);
      const items = r.data.items || [];
      setListItems(items);
      setShortlisted(Object.fromEntries(items.map((it) => [`${it.platform}:${it.externalId}`, true])));
    } catch { setListItems([]); }
    finally { setListLoading(false); }
  }, []);
  useEffect(() => { loadListItems(activeListId); }, [activeListId, loadListItems]);

  const has = (p) => platforms.includes(p);
  const togglePlatform = (p) => setPlatforms((cur) => {
    if (cur.includes(p)) return cur.length === 1 ? cur : cur.filter((x) => x !== p); // keep at least one
    return PLATFORMS.filter((x) => x === p || cur.includes(x));
  });
  useEffect(() => { if (!platforms.includes(activeTab)) setActiveTab(platforms[0]); }, [platforms, activeTab]);

  // Dropdown options: union across the selected platforms.
  useEffect(() => {
    let cancelled = false;
    Promise.all(platforms.map((p) => discoveryAPI.filters(p).then((r) => r.data).catch(() => null)))
      .then((all) => {
        if (cancelled) return;
        const merge = (key) => {
          const m = new Map();
          for (const d of all) for (const x of (d?.[key] || [])) m.set(x.code, (m.get(x.code) || 0) + x.n);
          return [...m.entries()].map(([code, n]) => ({ code, n })).sort((a, b) => b.n - a.n);
        };
        setMeta({ countries: merge('countries'), languages: merge('languages'), genres: merge('genres') });
      });
    return () => { cancelled = true; };
  }, [platforms]);

  // Game picker search (debounced).
  useEffect(() => {
    if (!gameQuery || gameQuery.trim().length < 2) { setGameResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        setGameSearching(true);
        const r = await discoveryAPI.searchGames(gameQuery.trim());
        setGameResults(r.data.games || []);
      } catch { setGameResults([]); }
      finally { setGameSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [gameQuery]);

  // Close the game dropdown on outside click.
  useEffect(() => {
    const h = (e) => { if (gameBoxRef.current && !gameBoxRef.current.contains(e.target)) setGameResults([]); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // The time window: a rolling "last N days" or an explicit range.
  const rangeActive = customRange && (from || to);
  const windowLabel = rangeActive
    ? `${from ? fmtDay(from) : 'start'} → ${to ? fmtDay(to) : 'today'}`
    : `Last ${days} days`;

  // Shared filters; each platform hook adds its own platform/sort/paging.
  const baseParams = useMemo(() => {
    const p = { onPlatform };
    if (rangeActive) { if (from) p.from = from; if (to) p.to = to; } else p.days = days;
    if (game?.id) p.gameId = game.id;
    if (language) p.language = language;
    if (country) p.country = country;
    if (genre) p.genre = genre;
    const mp = parseInt(minPeak, 10); if (Number.isFinite(mp) && mp > 0) p.minPeak = mp;
    const mv = parseInt(minViews, 10); if (Number.isFinite(mv) && mv > 0) p.minViews = mv;
    return p;
  }, [days, rangeActive, from, to, onPlatform, game, language, country, genre, minPeak, minViews]);

  // A search needs at least a game, a genre, a country or a language. Without
  // one it would aggregate every stream in the window for ~78,000 streamers —
  // several seconds for a list nobody asked for — so the page prompts instead.
  const canSearch = !!(game?.id || country || language || genre);

  const twitch = usePlatformSearch('twitch', baseParams, has('twitch'), canSearch);
  const youtube = usePlatformSearch('youtube', baseParams, has('youtube'), canSearch);
  const kick = usePlatformSearch('kick', baseParams, has('kick'), canSearch);
  const searches = { twitch, youtube, kick };
  const shown = platforms.length > 1 ? activeTab : platforms[0];
  const s = searches[shown];

  const pickGame = async (g) => {
    setGameQuery(''); setGameResults([]);
    if (g.source === 'platform') { setGame({ id: g.id, title: g.title, twitchGameId: g.twitchGameId }); return; }
    // A Twitch category we do not hold yet: create the Game row (one call), then search it.
    try {
      setGameAdding(true);
      const r = await discoveryAPI.addGame(g.twitchGameId, g.title);
      setGame({ id: r.data.game.id, title: r.data.game.title, twitchGameId: r.data.game.twitchGameId, justAdded: r.data.created });
    } catch (e) { setError(e.response?.data?.error || 'Could not add that game'); }
    finally { setGameAdding(false); }
  };

  const addToRoster = async (platform, row) => {
    const key = `${platform}:${row.externalId}`;
    if (adding[key] || added[key]) return;
    setAdding((a) => ({ ...a, [key]: true }));
    try {
      const r = await discoveryAPI.add(platform, row.externalId);
      setAdded((a) => ({ ...a, [key]: r.data.creatorId }));
      if (onAdded) onAdded(r.data);
    } catch (e) {
      const cid = e.response?.data?.creatorId;
      if (cid) setAdded((a) => ({ ...a, [key]: cid }));
      else setError(e.response?.data?.error || 'Could not add creator');
    } finally { setAdding((a) => { const n = { ...a }; delete n[key]; return n; }); }
  };

  // Why they were shortlisted — the search context and the row's numbers now.
  const contextFor = (platform, row) => ({
    gameId: game?.id || null, gameTitle: game?.title || null, genre: genre || null, windowLabel,
    metrics: isStreamPlatform(platform)
      ? { peak: row.peak, avgCcv: row.avgCcv, watchHours: row.watchHours, streams: row.streams, hours: row.hours, lastPlayed: row.lastPlayed }
      : { views: row.views, videos: row.videos, topViews: row.topViews, latest: row.latest, subscriberCount: row.subscriberCount },
  });

  const createList = async () => {
    const name = newListName.trim();
    if (!name) return;
    try {
      const r = await discoveryAPI.createShortlist(name);
      setNewListName('');
      await loadLists();
      setActiveListId(r.data.shortlist.id);
      setPanelOpen(true);
    } catch (e) { setError(e.response?.data?.error || 'Could not create shortlist'); }
  };

  const deleteList = async () => {
    const l = lists.find((x) => x.id === activeListId);
    if (!l) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the shortlist "${l.name}" (${l.items} creators)? The creators themselves are not affected.`)) return;
    try { await discoveryAPI.deleteShortlist(l.id); setActiveListId(''); await loadLists(); }
    catch (e) { setError(e.response?.data?.error || 'Could not delete shortlist'); }
  };

  const toggleShortlist = async (platform, row) => {
    if (!activeListId) { setPanelOpen(true); setListNotice('Create a shortlist first, then save creators to it with the bookmark on each row.'); return; }
    const key = `${platform}:${row.externalId}`;
    try {
      if (shortlisted[key]) {
        const it = listItems.find((x) => x.platform === platform && x.externalId === row.externalId);
        if (it) await discoveryAPI.shortlistRemove(activeListId, it.itemId);
      } else {
        await discoveryAPI.shortlistAdd(activeListId, platform, row.externalId, contextFor(platform, row));
      }
      await Promise.all([loadListItems(activeListId), loadLists()]);
    } catch (e) { setError(e.response?.data?.error || 'Shortlist update failed'); }
  };

  const removeItem = async (it) => {
    try { await discoveryAPI.shortlistRemove(activeListId, it.itemId); await Promise.all([loadListItems(activeListId), loadLists()]); }
    catch (e) { setError(e.response?.data?.error || 'Could not remove'); }
  };

  const describeEnrich = (r) => r.skipped
    ? `Contact lookup skipped: ${r.reason}.`
    : r.lookedUp
      ? `Looked up ${full(r.lookedUp)} creator${r.lookedUp === 1 ? '' : 's'} (~${r.units} API units): ${full(r.withEmail)} with an email, ${full(r.withLinks)} with social links.`
      : 'Contact details were already on file for everyone — no API used.';

  const exportList = async () => {
    const l = lists.find((x) => x.id === activeListId);
    if (!l) return;
    try {
      setListBusy('export'); setListNotice('Looking up contact details for anyone missing them, then building the CSV…');
      const r = await discoveryAPI.shortlistExport(l.id);
      let notice = 'Exported.';
      try { const enr = JSON.parse(r.headers['x-enrichment'] || 'null'); if (enr) notice = `Exported. ${describeEnrich(enr)}`; } catch { /* header optional */ }
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url; a.download = `shortlist-${l.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      setListNotice(notice);
      await loadListItems(l.id);
    } catch (e) { setError(e.response?.data?.error || 'Export failed'); setListNotice(''); }
    finally { setListBusy(''); }
  };

  const addAllToPlatform = async () => {
    const l = lists.find((x) => x.id === activeListId);
    if (!l) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Add all ${l.items} creators on "${l.name}" to the platform? Those already on it are skipped.`)) return;
    try {
      setListBusy('addAll');
      const r = await discoveryAPI.shortlistAddAll(l.id);
      setListNotice(`Added ${full(r.data.added)} to the platform; ${full(r.data.alreadyOnPlatform)} were already on it${r.data.failed ? `; ${r.data.failed} failed` : ''}.`);
      await loadListItems(l.id);
      if (onAdded) onAdded(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Add all failed'); }
    finally { setListBusy(''); }
  };
  const activeList = lists.find((x) => x.id === activeListId) || null;

  const clearAll = () => { setGame(null); setLanguage(''); setCountry(''); setGenre(''); setOnPlatform('off'); setMinPeak(''); setMinViews(''); setDays(60); setCustomRange(false); setFrom(''); setTo(''); };
  const hasFilters = game || language || country || genre || minPeak || minViews || onPlatform !== 'off' || days !== 60 || rangeActive;
  // What the numbers refer to.
  const scopeLabel = game ? game.title : genre ? `${genre}` : 'tracked-game';
  const windowText = rangeActive ? `between ${windowLabel.replace(' → ', ' and ')}` : `in the last ${days} days`;

  const summaryFor = (p) => {
    const sp = searches[p];
    const isTwitch = isStreamPlatform(p);
    if (sp.loading) return 'Searching…';
    return (
      <>
        <span className="font-semibold">{full(sp.total)}</span> {PLATFORM_LABEL[p]} {isTwitch ? 'streamers' : 'channels'}
        {game ? <> who {isTwitch ? 'streamed' : 'posted'} <span className="font-semibold">{game.title}</span></> : ' active'} {windowText}
        {genre ? <> · {genre} games</> : null}
        {country ? <> · {getCountryFlag(country)} {countryName(country)}</> : null}
        {language ? <> · {languageName(language)}</> : null}
        {onPlatform === 'off' ? ' · not yet on the platform' : onPlatform === 'on' ? ' · already on the platform' : ''}
      </>
    );
  };

  return (
    <div>
      {/* Filters */}
      <div className="bg-gray-900/60 border border-white rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Platforms — one or both */}
          <div className="flex items-center gap-1.5" title="Pick one or both">
            {PLATFORMS.map((p) => (
              <button key={p} onClick={() => togglePlatform(p)} className={`${segBtn(has(p))} inline-flex items-center gap-1.5`}>
                <PlatformIcon platform={p} size="sm" variant={has(p) ? 'color' : 'mono'} />
                {PLATFORM_LABEL[p]}
              </button>
            ))}
          </div>

          {/* Game picker */}
          <div className="relative flex-1 min-w-[240px] max-w-md" ref={gameBoxRef}>
            {game ? (
              <div className="flex items-center gap-2 bg-sc-pink/10 border border-sc-pink/30 text-sc-pink rounded-lg px-3 py-1.5 text-xs">
                <Gamepad2 className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate font-medium">{game.title}</span>
                {game.justAdded && <span className="text-[10px] text-white/80">· added to platform</span>}
                <button onClick={() => setGame(null)} className="ml-auto hover:text-white" title="Clear game"><X className="w-3.5 h-3.5" /></button>
              </div>
            ) : (
              <>
                <Search className="w-3.5 h-3.5 text-white absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  value={gameQuery}
                  onChange={(e) => setGameQuery(e.target.value)}
                  placeholder={gameAdding ? 'Adding game…' : 'Any game — type to search (Twitch categories too)'}
                  disabled={gameAdding}
                  className={`w-full pl-8 pr-3 ${inputCls}`}
                />
                {(gameSearching || gameResults.length > 0) && gameQuery.trim().length >= 2 && (
                  <div className="absolute z-20 mt-1 w-full bg-[#241d33] border border-white rounded-xl shadow-xl overflow-hidden">
                    {gameSearching && gameResults.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-white flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</div>
                    ) : gameResults.map((g) => (
                      <button
                        key={`${g.source}-${g.id || g.twitchGameId}`}
                        onClick={() => pickGame(g)}
                        className="w-full text-left px-3 py-2 text-xs text-white hover:bg-white/10 flex items-center gap-2"
                      >
                        <Gamepad2 className="w-3.5 h-3.5 text-sc-pink flex-shrink-0" />
                        <span className="truncate">{g.title}</span>
                        {g.source === 'twitch' ? (
                          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/20 border border-purple-400/30 text-white whitespace-nowrap">not on platform · add</span>
                        ) : g.recentStreams > 0 ? (
                          <span className="ml-auto text-[10px] text-white/80 whitespace-nowrap">{full(g.recentStreams)} streams · 90d</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Show */}
          <div className="flex items-center gap-1.5">
            {SHOW.map((x) => (
              <button key={x.key} onClick={() => setOnPlatform(x.key)} className={segBtn(onPlatform === x.key)}>{x.label}</button>
            ))}
          </div>
        </div>

        {/* Window: rolling days or an explicit range */}
        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          <span className="text-[11px] text-white mr-1">Last</span>
          {WINDOWS.map((d) => (
            <button key={d} onClick={() => { setDays(d); setCustomRange(false); }} className={segBtn(!rangeActive && !customRange && days === d)}>{d}d</button>
          ))}
          <button onClick={() => setCustomRange((c) => !c)} className={`${segBtn(customRange)} inline-flex items-center gap-1.5`}>
            <CalendarDays className="w-3.5 h-3.5" /> Custom dates
          </button>
          {customRange && (
            <div className="flex items-center gap-1.5 ml-1">
              <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
              <span className="text-[11px] text-white">to</span>
              <input type="date" value={to} min={from || undefined} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setTo(e.target.value)} className={inputCls} />
              {(from || to) && <button onClick={() => { setFrom(''); setTo(''); }} className="text-white hover:text-sc-pink" title="Clear dates"><X className="w-3.5 h-3.5" /></button>}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-white/10">
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className={selectCls}>
            <option value="">Any language</option>
            {meta.languages.map((l) => (
              <option key={l.code} value={l.code}>{languageName(l.code)}</option>
            ))}
          </select>
          {/* No counts on the options: they would be pool-wide totals, which read
              as "2.5K match" next to a result line saying 11 do. */}
          <select value={country} onChange={(e) => setCountry(e.target.value)} className={selectCls}>
            <option value="">Any country</option>
            {meta.countries.map((c) => (
              <option key={c.code} value={c.code}>{getCountryFlag(c.code)} {countryName(c.code)}</option>
            ))}
          </select>
          {/* Game genre (IGDB): "who streams RPGs / shooters" without naming a title. */}
          <select value={genre} onChange={(e) => setGenre(e.target.value)} className={selectCls} title="Genre of the games they played/posted in the window">
            <option value="">Any genre</option>
            {meta.genres.map((g) => (
              <option key={g.code} value={g.code}>{g.code}</option>
            ))}
          </select>
          {(has('twitch') || has('kick')) && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-white whitespace-nowrap">Min peak viewers{has('youtube') ? (has('twitch') && has('kick') ? ' (Twitch/Kick)' : has('kick') ? ' (Kick)' : ' (Twitch)') : ''}</span>
              <input type="number" min="0" value={minPeak} onChange={(e) => setMinPeak(e.target.value)} placeholder="0" className={`w-24 ${inputCls}`} />
            </div>
          )}
          {has('youtube') && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-white whitespace-nowrap">Min views{(has('twitch') || has('kick')) ? ' (YouTube)' : ''}</span>
              <input type="number" min="0" value={minViews} onChange={(e) => setMinViews(e.target.value)} placeholder="0" className={`w-24 ${inputCls}`} />
            </div>
          )}
          {hasFilters && (
            <button onClick={clearAll} className="ml-auto inline-flex items-center gap-1 text-xs text-white hover:text-sc-pink">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Shortlist bar: which list the bookmark buttons save to, and the panel toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
        <span className="text-white">Save to shortlist:</span>
        <select value={activeListId} onChange={(e) => setActiveListId(e.target.value)} className={selectCls}>
          {lists.length === 0 && <option value="">— none yet —</option>}
          {lists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.items})</option>)}
        </select>
        <input
          value={newListName} onChange={(e) => setNewListName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') createList(); }}
          placeholder="New shortlist name" className={`w-44 ${inputCls}`}
        />
        <button onClick={createList} disabled={!newListName.trim()} className={`${segBtn(false)} disabled:opacity-50`}>Create</button>
        <button onClick={() => setPanelOpen((o) => !o)} className={`${segBtn(panelOpen)} ml-auto inline-flex items-center gap-1.5`}>
          <Bookmark className="w-3.5 h-3.5" />
          {panelOpen ? 'Hide shortlist' : `Open shortlist${activeList ? ` (${activeList.items})` : ''}`}
        </button>
      </div>

      {panelOpen && (
        <div className="bg-gray-900/60 border border-white rounded-xl mb-4">
          <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-white/10">
            <BookmarkCheck className="w-4 h-4 text-sc-pink" />
            <span className="text-white font-semibold">{activeList ? activeList.name : 'No shortlist selected'}</span>
            {activeList && <span className="text-xs text-white/80">· {full(listItems.length)} creator{listItems.length === 1 ? '' : 's'} · {full(listItems.filter((i) => i.contactEmail).length)} with an email</span>}
            {activeList && (
              <div className="ml-auto flex items-center gap-2">
                <button onClick={exportList} disabled={!!listBusy || !listItems.length}
                  title="Looks up contact details for anyone missing them (their own bio, official APIs, capped per day), then downloads the CSV"
                  className={`${segBtn(false)} inline-flex items-center gap-1.5 disabled:opacity-50`}>
                  {listBusy === 'export' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export CSV
                </button>
                <button onClick={addAllToPlatform} disabled={!!listBusy || !listItems.length}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 text-xs font-semibold disabled:opacity-50">
                  {listBusy === 'addAll' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Add all to Platform
                </button>
                <button onClick={deleteList} disabled={!!listBusy} title="Delete this shortlist (creators are not affected)"
                  className="px-2.5 py-1.5 rounded-lg bg-white/5 border border-white text-white hover:bg-white/10 disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
          {listNotice && <div className="px-4 py-2 text-xs text-white border-b border-white/10">{listNotice}</div>}
          {listLoading ? (
            <div className="flex items-center justify-center py-8 text-white"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : !activeList ? (
            <div className="px-4 py-6 text-sm text-white/80">Create a shortlist above, then use the bookmark on any row to save creators to it.</div>
          ) : listItems.length === 0 ? (
            <div className="px-4 py-6 text-sm text-white/80">Empty. Use the bookmark on any row below to save creators here.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-white/80">
                    <Th label="Creator" className="text-left pl-4" />
                    <Th label="Country" className="text-left" />
                    <Th label="Shortlisted for" className="text-left" title="The search this creator was saved from" />
                    <Th label="Email" className="text-left" />
                    <Th label="Socials" className="text-left" />
                    <Th label="Subscribers" />
                    <Th label="Status" className="text-left" />
                    <Th label="" className="pr-4" />
                  </tr>
                </thead>
                <tbody>
                  {listItems.map((it) => {
                    const links = it.socialLinks || {};
                    const socials = Object.entries(links).filter(([, v]) => typeof v === 'string' && v);
                    const why = [it.gameTitle, it.genre ? `${it.genre} games` : null].filter(Boolean).join(' · ');
                    return (
                      <tr key={it.itemId} className="border-t border-white/10 hover:bg-white/5">
                        <td className="pl-4 py-2 pr-3">
                          <div className="flex items-center gap-2 min-w-[180px]">
                            <PlatformIcon platform={it.platform} size="sm" />
                            <a href={it.url} target="_blank" rel="noreferrer" className="text-white font-semibold hover:text-sc-pink truncate max-w-[220px]">{it.title || it.handle || it.externalId}</a>
                            {it.onPlatform && <span title="On the platform" className="text-emerald-400"><Check className="w-3.5 h-3.5" /></span>}
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-xs text-white">{it.country ? <>{getCountryFlag(it.country)} {countryName(it.country)}</> : <span className="text-white/40">—</span>}</td>
                        <td className="py-2 pr-3 text-xs text-white" title={it.windowLabel || ''}>
                          {why || <span className="text-white/40">—</span>}{it.windowLabel ? <span className="text-white/60"> · {it.windowLabel}</span> : null}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          {it.contactEmail ? <a href={`mailto:${it.contactEmail}`} className="text-white hover:text-sc-pink">{it.contactEmail}</a>
                            : it.enrichedAt ? <span className="text-white/40" title="Checked their bio, recent video descriptions, any link page they name, and the same handle on the other platform — no public email">not published</span>
                              : <span className="text-white/40" title="Looked up when you export">on export</span>}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          <div className="flex items-center gap-1.5">
                            {socials.length ? socials.map(([k, v]) => (
                              <a key={k} href={v} target="_blank" rel="noreferrer" className="px-1.5 py-0.5 rounded bg-white/5 border border-white/20 text-white hover:border-sc-pink">{k.replace(/Url$/, '')}</a>
                            )) : <span className="text-white/40">—</span>}
                          </div>
                        </td>
                        <Td>{full(it.subscriberCount)}</Td>
                        <td className="py-2 pr-3 text-xs text-white">{it.onPlatform ? 'On platform' : 'Not on platform'}</td>
                        <td className="py-2 pl-2 pr-4 text-right">
                          <button onClick={() => removeItem(it)} title="Remove from shortlist" className="px-2 py-1 rounded-lg bg-white/5 border border-white text-white hover:bg-white/10"><X className="w-3.5 h-3.5" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-white flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-white hover:text-sc-pink"><X className="w-4 h-4" /></button>
        </div>
      )}

      {!canSearch ? (
        <div className="text-center py-16 bg-gray-900/60 border border-white rounded-xl">
          <Compass className="w-8 h-8 text-sc-pink mx-auto mb-3" />
          <p className="text-white font-medium">Who are you looking for?</p>
          <p className="text-white/80 text-sm mt-1">Choose a game, a genre, a country or a language above.</p>
        </div>
      ) : (
        <div className="bg-gray-900/60 border border-white rounded-xl overflow-hidden">
          {/* Platform tabs (only when both are selected) — part of the results box */}
          {platforms.length > 1 && (
            <div className="flex items-end gap-1 px-3 pt-2 border-b border-white/10">
              {platforms.map((p) => (
                <button
                  key={p}
                  onClick={() => setActiveTab(p)}
                  className={`inline-flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
                    shown === p ? 'border-sc-pink text-sc-pink' : 'border-transparent text-white hover:text-sc-pink'
                  }`}
                >
                  <PlatformIcon platform={p} size="sm" variant={shown === p ? 'color' : 'mono'} />
                  {PLATFORM_LABEL[p]}
                  <span className={`text-xs ${shown === p ? 'text-sc-pink' : 'text-white/80'}`}>{searches[p].loading ? '…' : full(searches[p].total)}</span>
                </button>
              ))}
            </div>
          )}

          {/* Summary for the visible table */}
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 text-xs text-white border-b border-white/10">
            {platforms.length === 1 && <PlatformIcon platform={shown} size="sm" />}
            <span>{summaryFor(shown)}</span>
          </div>

          {s.error && <div className="px-4 py-2 text-sm text-white bg-red-500/10 border-b border-red-500/30">{s.error}</div>}

          {s.loading ? (
            <div className="flex items-center justify-center py-12 text-white"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : s.results.length === 0 ? (
            <div className="text-center py-10">
              <Search className="w-6 h-6 text-white mx-auto mb-2" />
              <p className="text-white font-medium">No {PLATFORM_LABEL[shown]} {isStreamPlatform(shown) ? 'streamers' : 'channels'} match</p>
              <p className="text-white/80 text-sm mt-1">
                {game ? `Nobody in the pool ${isStreamPlatform(shown) ? 'streamed' : 'posted'} ${game.title} with these filters ${windowText}.` : 'Try a wider window or fewer filters.'}
                {shown === 'kick' && game ? ' Kick coverage is sampled hourly and started on 6 September 2026.' : ''}
                {game?.justAdded && shown === 'twitch' ? ' This game was just added — the 10-minute scan starts collecting its streams from now.' : ''}
              </p>
            </div>
          ) : (
            <>
              <ResultsTable
                platform={shown} rows={s.results} sort={s.sort} dir={s.dir} onSort={s.sortBy}
                game={game} windowText={windowText} scopeLabel={scopeLabel}
                added={added} adding={adding} onAdd={(row) => addToRoster(shown, row)}
                onView={(creatorId) => navigate(`/dashboard/creators/${creatorId}`)}
                onShortlist={(row) => toggleShortlist(shown, row)} shortlisted={shortlisted}
              />
              <Pagination page={s.page} pages={s.pages} total={s.total} onPage={s.setPage} />
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Numbered pages. The server orders the whole result, so page 7 sorted by
// watch hours is exactly rows 301-350 of that order.
const Pagination = ({ page, pages, total, onPage }) => {
  if (pages <= 1) return (
    <div className="px-4 py-2.5 text-xs text-white/80 border-t border-white/10">{full(total)} result{total === 1 ? '' : 's'}</div>
  );
  const nums = [];
  const push = (n) => { if (!nums.includes(n)) nums.push(n); };
  push(1); push(pages);
  for (let n = page - 2; n <= page + 2; n++) if (n >= 1 && n <= pages) push(n);
  nums.sort((a, b) => a - b);
  const btn = 'min-w-[32px] px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors';
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-t border-white/10">
      <span className="text-xs text-white/80">
        Showing {full((page - 1) * PAGE_SIZE + 1)}–{full(Math.min(page * PAGE_SIZE, total))} of {full(total)}
      </span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} className={`${btn} bg-white/5 border-white text-white hover:bg-white/10 disabled:opacity-40`} title="Previous page"><ChevronLeft className="w-3.5 h-3.5" /></button>
        {nums.map((n, i) => (
          <React.Fragment key={n}>
            {i > 0 && nums[i - 1] !== n - 1 && <span className="px-1 text-xs text-white/60">…</span>}
            <button onClick={() => onPage(n)} className={`${btn} ${n === page ? 'bg-sc-pink/20 border-sc-pink/30 text-sc-pink' : 'bg-white/5 border-white text-white hover:bg-white/10'}`}>{n}</button>
          </React.Fragment>
        ))}
        <button onClick={() => onPage(page + 1)} disabled={page >= pages} className={`${btn} bg-white/5 border-white text-white hover:bg-white/10 disabled:opacity-40`} title="Next page"><ChevronRight className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
};

// The table for one platform. Column headers sort server-side (the list is
// paged), so clicking "Watch hours" orders the whole result, not just the rows
// on this page.
const ResultsTable = ({ platform, rows, sort, dir, onSort, game, windowText, scopeLabel, added, adding, onAdd, onView, onShortlist, shortlisted }) => {
  const isTwitch = isStreamPlatform(platform); // stream-shaped columns (Twitch and Kick)
  const isKick = platform === 'kick';
  const th = (k, label, title) => <Th k={k} label={label} title={title} sort={sort} dir={dir} onSort={onSort} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm whitespace-nowrap">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-white/80">
            <Th label="Creator" className="text-left pl-4" />
            <Th label="Country" className="text-left" />
            {isTwitch && <Th label="Language" className="text-left" />}
            <Th label="Games" className="text-left" title={game ? 'Everything they played in the window, not just the game searched' : 'Games in the window, most-watched first'} />
            {isTwitch ? (
              <>
                {th('peak', 'Peak CCV', `Highest concurrent viewers on a ${scopeLabel} stream ${windowText}`)}
                {th('avg', 'Avg CCV', `Average concurrent viewers across their ${scopeLabel} streams ${windowText}`)}
                {th('watch', 'Watch hours', `Hours watched by the audience: average viewers × hours live, summed over their ${scopeLabel} streams ${windowText}`)}
                {th('streams', 'Streams', `${scopeLabel} streams ${windowText}`)}
                {th('hours', 'Hours streamed', `Hours the creator was live on ${scopeLabel} ${windowText} (airtime, not watch time)`)}
                {th('recent', 'Last streamed')}
                <Th label="Data" title="Tracked = sampled every 10 minutes while live. Snapshot = seen by the directory sweep only, one viewer reading per stream." />
              </>
            ) : (
              <>
                {th('views', 'Views', `Lifetime views (as of our last refresh) of their ${scopeLabel} videos published ${windowText} — not views gained in the window`)}
                {th('videos', 'Videos', `${scopeLabel} videos published ${windowText}`)}
                {th('topviews', 'Top video', `Views on their single biggest ${scopeLabel} video from ${windowText}`)}
                {th('subs', 'Subscribers', 'Channel subscribers (whole channel, not the game)')}
                {th('recent', 'Latest upload')}
              </>
            )}
            <Th label="" className="pr-4" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = `${platform}:${r.externalId}`;
            const creatorId = added[key] || r.creatorId;
            const isOn = !!creatorId;
            return (
              <tr key={r.id} className="border-t border-white/10 hover:bg-white/5 align-middle">
                <td className="pl-4 py-2.5 pr-3">
                  <div className="flex items-center gap-2.5 min-w-[200px] max-w-[260px]">
                    {r.avatarUrl ? (
                      <img src={r.avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover border border-white/20 flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-sc-pink/15 border border-sc-pink/25 text-sc-pink flex items-center justify-center font-bold text-xs flex-shrink-0">
                        {(r.title || r.handle || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <PlatformIcon platform={platform} size="sm" />
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-white font-semibold truncate hover:text-sc-pink" title={`Open on ${PLATFORM_LABEL[platform]}`}>{r.title || r.handle}</a>
                        {isOn && <span title="On the platform" className="text-emerald-400 flex-shrink-0"><Check className="w-3.5 h-3.5" /></span>}
                      </div>
                      {r.handle && <div className="text-[11px] text-white/80 truncate">@{String(r.handle).replace(/^@+/, '')}</div>}
                    </div>
                  </div>
                </td>
                <td className="py-2.5 pr-3 text-white text-xs" title={r.countrySource === 'language' ? 'Country inferred from stream language' : r.country ? 'Country from the creator\'s own tags/profile' : ''}>
                  {r.country ? <>{getCountryFlag(r.country)} {countryName(r.country)}{r.countrySource === 'language' ? <span className="text-white/60"> ≈</span> : null}</> : <span className="text-white/40">—</span>}
                </td>
                {isTwitch && (
                  <td className="py-2.5 pr-3 text-xs">
                    {r.language ? <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-sc-pink/10 border border-sc-pink/15 text-sc-pink">{languageName(r.language)}</span> : <span className="text-white/40">—</span>}
                  </td>
                )}
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-1">
                    {(r.topGames || []).slice(0, 6).map((g) => (
                      <a
                        key={g.gameId}
                        href={isKick ? `https://kick.com/${r.handle}/videos` : isTwitch ? `https://twitch.tv/${r.handle}/videos?filter=archives` : (g.topVideoId ? `https://www.youtube.com/watch?v=${g.topVideoId}` : r.url)}
                        target="_blank" rel="noreferrer"
                        title={isTwitch
                          ? `${g.title} · ${full(g.streams)} stream${g.streams === 1 ? '' : 's'} · ${full(g.peak)} peak · last ${fmtDate(g.lastPlayed)}`
                          : `${g.title} · ${full(g.videos)} video${g.videos === 1 ? '' : 's'} · ${full(g.views)} views · latest ${fmtDate(g.latest)}${g.topVideoTitle ? `\nOpens: ${g.topVideoTitle}` : ''}`}
                        className="block w-7 h-9 rounded overflow-hidden border border-white/20 bg-white/5 hover:border-sc-pink flex-shrink-0"
                      >
                        <GameCover game={g} />
                      </a>
                    ))}
                  </div>
                </td>
                {isTwitch ? (
                  <>
                    <Td>{full(r.peak)}</Td>
                    <Td>{full(r.avgCcv)}</Td>
                    <Td>{full(r.watchHours)}</Td>
                    <Td>{full(r.streams)}</Td>
                    <Td>{full(r.hours, 1)}</Td>
                    <Td>{fmtDate(r.lastPlayed)}</Td>
                    <td className="py-2.5 pr-3 text-[11px] text-white/80">
                      {r.estimate
                        ? <span className="inline-flex items-center gap-1" title="Seen by the directory sweep only — one viewer reading per stream, so peak/average are snapshots rather than tracked history"><Camera className="w-3 h-3" /> snapshot</span>
                        : <span className="inline-flex items-center gap-1" title={isKick ? 'Sampled hourly by the Kick directory sweep while live' : 'Tracked every 10 minutes while live'}><Radio className="w-3 h-3" /> tracked</span>}
                    </td>
                  </>
                ) : (
                  <>
                    <Td>{full(r.views)}</Td>
                    <Td>{full(r.videos)}</Td>
                    <Td>{full(r.topViews)}</Td>
                    <Td>{full(r.subscriberCount)}</Td>
                    <Td>{fmtDate(r.latest)}</Td>
                  </>
                )}
                <td className="py-2 pl-2 pr-4 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      onClick={() => onShortlist(r)}
                      title={shortlisted[key] ? 'Remove from the selected shortlist' : 'Save to the selected shortlist'}
                      className={`px-2 py-1.5 rounded-lg border text-xs ${shortlisted[key] ? 'bg-sc-pink/20 border-sc-pink/30 text-sc-pink' : 'bg-white/5 border-white text-white hover:bg-white/10'}`}
                    >
                      {shortlisted[key] ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
                    </button>
                    {isOn ? (
                      <button onClick={() => onView(creatorId)}
                        className="px-3 py-1.5 rounded-lg bg-white/5 border border-white text-white hover:bg-white/10 text-xs font-medium whitespace-nowrap">
                        View profile
                      </button>
                    ) : (
                      <button onClick={() => onAdd(r)} disabled={!!adding[key]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sc-pink/15 border border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 text-xs font-semibold disabled:opacity-50 whitespace-nowrap">
                        {adding[key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        Add to Platform
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Game cover: our IGDB cover first, then Twitch's public box art for the
// category (same CDN the Twitch directory itself uses), then a lettered tile.
const GameCover = ({ game }) => {
  const [step, setStep] = useState(0);
  const sources = [
    game.coverImageUrl,
    game.twitchGameId ? `https://static-cdn.jtvnw.net/ttv-boxart/${game.twitchGameId}-72x96.jpg` : null,
  ].filter(Boolean);
  const src = sources[step];
  if (!src) {
    return (
      <div className="w-full h-full flex items-center justify-center text-sc-pink font-bold text-sm bg-sc-pink/10">
        {(game.title || '?').slice(0, 1).toUpperCase()}
      </div>
    );
  }
  return <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" onError={() => setStep((x) => x + 1)} />;
};

// Table header cell. With a sort key `k` it is clickable: first click sorts
// descending (biggest first — what you want for viewers/hours), a second click
// flips it. The active column shows its arrow.
const Th = ({ k, label, title, sort, dir, onSort, className = '' }) => {
  const sortable = !!k;
  const active = sortable && sort === k;
  return (
    <th
      className={`py-2.5 pr-3 font-medium select-none ${sortable ? 'text-right cursor-pointer hover:text-sc-pink' : ''} ${active ? 'text-sc-pink' : ''} ${className}`}
      title={title}
      onClick={sortable ? () => onSort(k) : undefined}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (dir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
        {sortable && !active && <ArrowUpDown className="w-3 h-3 opacity-40" />}
      </span>
    </th>
  );
};

const Td = ({ children }) => (
  <td className="py-2.5 pr-3 text-right text-white font-medium tabular-nums">{children}</td>
);

export default CreatorDiscovery;
